// src/components/enterprise/AnalystPanel.jsx
//
// The analyst tab: a question in plain words, answered from this organization's
// warehouse rows at five or more per group. The model writes the SQL; the server
// checks it (one SELECT, aggregates only, this organization's filter, the privacy
// count) and drops any row built from fewer than five people before anything
// returns (functions/modules/analyst.js). This panel asks, shows the answer as a
// chart with the analyst's one-line reading, and shows the query it ran, so
// nothing here is a black box. Earlier questions are kept per organization in
// this browser only. Replaced the chat-style CustomDataAnalyst on 2026-11-03.

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Send, Loader2, Trash2, Building2, ChevronDown, ChevronUp, Lock, Clock3 } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { httpsCallable } from 'firebase/functions';
import { collection, getDocs } from 'firebase/firestore';
import { functions, db } from '../../config/firebase-config';
import { isSuperAdmin } from '../../config/permissions';
import { useEdition } from '../../data/editions/EditionContext';
import { useOrganization } from '../../context/OrganizationContext';
import { ConfidenceTag, SourceTag, DisciplineFooter, MIN_GROUP_SIZE } from './shared/measurement';

// Example questions follow the console edition: a board asks by sector, a
// police service by unit, a sport body by team. The analyst answers all of
// them from the warehouse under the same floor.
const PROMPTS_BY_EDITION = {
    default:        ['Which teams are running hot this month?', 'Sleep quality by team, last 8 weeks', 'Stress trend across the organization', 'Focus and reaction time by team'],
    corporate:      ['Which teams are running hot this month?', 'Sleep quality by department, last 8 weeks', 'Stress trend across the organization', 'Focus over time'],
    insurance:      ['Which teams are running hot this month?', 'Sleep quality by department, last 8 weeks', 'Stress trend across the organization', 'Focus over time'],
    firstresponder: ['Which units are running hot this month?', 'Sleep quality by unit, last 8 weeks', 'Stress trend, service-wide', 'Focus and reaction time by unit'],
    compensation:   ['Which sectors are running hot this month?', 'Wellbeing score by sector, last 8 weeks', 'Anxiety by sector this month', 'Readiness trend across all sectors'],
    responsible_gaming: ['Gambling screen by region this month', 'Sleep quality by region', 'Anxiety trend, province-wide', 'Readiness by region, last 8 weeks'],
    schools:        ['Which schools are running hot this month?', 'Sleep quality by school, last 8 weeks', 'Anxiety by school this month', 'Readiness trend across the division'],
    community:      ['Which communities are running hot this month?', 'Sleep quality by community, last 8 weeks', 'Anxiety trend across communities', 'Readiness by community']
};
const promptsFor = (editionId) => PROMPTS_BY_EDITION[editionId] || PROMPTS_BY_EDITION.default;

const HISTORY_MAX = 8;
const historyKey = (orgId) => `erihub_analyst_${orgId || 'none'}`;
const readHistory = (orgId) => { try { const s = localStorage.getItem(historyKey(orgId)); return s ? JSON.parse(s) : []; } catch (e) { return []; } };
const writeHistory = (orgId, items) => { try { localStorage.setItem(historyKey(orgId), JSON.stringify(items.slice(0, HISTORY_MAX))); } catch (e) { /* storage unavailable */ } };
const sessionIdFor = () => { try { const s = localStorage.getItem('erihub_session_id'); if (s) return s; const n = `session-${Date.now()}`; localStorage.setItem('erihub_session_id', n); return n; } catch (e) { return `session-${Date.now()}`; } };

const when = (iso) => { try { return new Date(iso).toLocaleString('en-CA', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); } catch (e) { return ''; } };

// The chart the analyst's payload describes: { chartType, data, config: { dataKeys, xAxis, useDualAxis } }.
const AnswerChart = ({ result, colors }) => {
    const rows = result.data || [];
    if (!rows.length) return null;
    const sample = rows[0];
    const xAxisKey = result.config?.xAxis
        || Object.keys(sample).find(k => k.toLowerCase().includes('date') || k.toLowerCase().includes('time') || k === 'name')
        || Object.keys(sample)[0];
    const dataKeys = (result.config?.dataKeys?.length ? result.config.dataKeys : Object.keys(sample).filter(k => k !== xAxisKey))
        .filter(k => k !== 'n_users');
    const useDualAxis = !!result.config?.useDualAxis;
    const axisFor = (key) => {
        if (!useDualAxis) return 'left';
        const vals = rows.map(r => r[key]).filter(v => typeof v === 'number');
        return (vals.length ? Math.max(...vals) : 0) <= 20 ? 'right' : 'left';
    };
    const tick = { fontSize: 11, fill: '#94a3b8' };
    const tooltip = { borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '12px' };
    const isLine = (result.type || 'bar') === 'line';
    return (
        <div className="h-72 w-full">
            <ResponsiveContainer width="100%" height="100%">
                {isLine ? (
                    <LineChart data={rows} margin={{ top: 10, right: 20, bottom: 5, left: -10 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                        <XAxis dataKey={xAxisKey} tick={tick} axisLine={false} tickLine={false} />
                        <YAxis yAxisId="left" tick={tick} axisLine={false} tickLine={false} />
                        {useDualAxis && <YAxis yAxisId="right" orientation="right" tick={tick} axisLine={false} tickLine={false} />}
                        <Tooltip contentStyle={tooltip} />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        {dataKeys.map((key, i) => <Line key={key} yAxisId={axisFor(key)} type="monotone" dataKey={key} stroke={colors[i % colors.length]} strokeWidth={2} dot={false} />)}
                    </LineChart>
                ) : (
                    <BarChart data={rows} margin={{ top: 10, right: 20, bottom: 5, left: -10 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                        <XAxis dataKey={xAxisKey} tick={tick} axisLine={false} tickLine={false} />
                        <YAxis yAxisId="left" tick={tick} axisLine={false} tickLine={false} />
                        {useDualAxis && <YAxis yAxisId="right" orientation="right" tick={tick} axisLine={false} tickLine={false} />}
                        <Tooltip contentStyle={tooltip} />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        {dataKeys.map((key, i) => <Bar key={key} yAxisId={axisFor(key)} dataKey={key} fill={colors[i % colors.length]} radius={[4, 4, 0, 0]} />)}
                    </BarChart>
                )}
            </ResponsiveContainer>
        </div>
    );
};

const AnalystPanel = ({ currentUser }) => {
    const edition = useEdition();
    const { orgData, isPreviewMode } = useOrganization();
    const isSuper = isSuperAdmin(currentUser);
    const prompts = promptsFor(edition?.id);
    const colors = useMemo(() => [edition.palette.blue, edition.palette.yellow, edition.palette.deep, '#16a34a', '#dc2626', '#9333ea'], [edition]);

    // Which organization the analyst reads. An organization's admin reads their
    // own; the super admin reads the one on screen (the previewed one, in
    // preview) and may pick another outside preview.
    const [orgs, setOrgs] = useState([]);
    const [selectedOrgId, setSelectedOrgId] = useState(orgData?.id || currentUser?.organizationId || '');
    useEffect(() => { if (orgData?.id) setSelectedOrgId(orgData.id); }, [orgData?.id]);
    useEffect(() => {
        if (!isSuper || isPreviewMode) return;
        getDocs(collection(db, 'organizations'))
            .then(snap => setOrgs(snap.docs.map(d => ({ id: d.id, name: d.data().name || d.id })).sort((a, b) => a.name.localeCompare(b.name))))
            .catch(() => setOrgs([]));
    }, [isSuper, isPreviewMode]);
    const targetOrgId = isSuper ? selectedOrgId : (currentUser?.organizationId || '');

    const [question, setQuestion] = useState('');
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [history, setHistory] = useState(() => readHistory(orgData?.id || currentUser?.organizationId));
    const [showSql, setShowSql] = useState(false);
    const inputRef = useRef(null);
    useEffect(() => { setHistory(readHistory(targetOrgId)); setResult(null); setShowSql(false); }, [targetOrgId]);

    const remember = (item) => {
        const next = [item, ...history.filter(h => h.at !== item.at)].slice(0, HISTORY_MAX);
        setHistory(next);
        writeHistory(targetOrgId, next);
    };
    const clearHistory = () => { setHistory([]); writeHistory(targetOrgId, []); setResult(null); };

    const ask = async (text) => {
        const q = (typeof text === 'string' ? text : question).trim();
        if (!q || busy) return;
        if (!targetOrgId) { setResult({ question: q, at: new Date().toISOString(), error: 'Pick an organization first.' }); return; }
        setBusy(true); setShowSql(false);
        if (typeof text !== 'string') setQuestion('');
        try {
            // The organization on screen, never a group: the server locks a coach to
            // their own squad from their profile, and an admin reads the whole
            // organization. A group left on the super admin's own profile (a demo
            // seat, a View-as session) once narrowed every answer to that group.
            const res = await httpsCallable(functions, 'dataAnalyst')({
                message: q, sessionId: sessionIdFor(), previewOrgId: targetOrgId
            });
            const d = res.data || {};
            // A text_only answer is the server declining or failing (no rows, the privacy
            // count missing, a query that would not run): a notice, with the attempt and
            // the reason under the disclosure, never a chart.
            const notice = d.type === 'text_only';
            const item = {
                question: q, at: new Date().toISOString(),
                notice,
                text: d.text || 'The query ran and returned nothing to say.',
                type: d.payload?.chartType || d.chartType || 'bar',
                data: notice ? [] : (d.payload?.data || d.data || []),
                config: d.payload?.config || null,
                sql: d.payload?.debug_info?.sql || d.debug_info?.sql || null,
                reason: d.debug_info?.error || null
            };
            setResult(item);
            if (item.data.length) remember(item);
        } catch (e) {
            setResult({ question: q, at: new Date().toISOString(), error: e?.message || 'The analyst could not be reached.' });
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 no-print">
            {/* ASK */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 md:p-8">
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-5">
                    <div>
                        <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="text-base font-semibold text-[var(--brand-deep)]">Ask the warehouse</h3>
                            <SourceTag modeled={false} />
                            <ConfidenceTag level="Leading" />
                        </div>
                        <p className="text-xs text-slate-500 font-light mt-1 max-w-2xl leading-relaxed">
                            A question in plain words, answered from this organization's check-ins and its own figures, by group at {MIN_GROUP_SIZE} or more.
                            The analyst writes the query; the server checks it before it runs and drops any row built from fewer than {MIN_GROUP_SIZE} people. The query it ran is shown with every answer.
                        </p>
                    </div>
                    {isSuper && (
                        isPreviewMode
                            ? <p className="text-xs text-slate-500 font-medium flex items-center gap-1.5 shrink-0"><Building2 size={14} className="text-slate-400" /> Reading {orgData?.name || 'this organization'}</p>
                            : (
                                <label className="flex items-center gap-2 text-xs font-medium text-slate-600 shrink-0">
                                    <Building2 size={14} className="text-slate-400" />
                                    <select className="bg-white border border-slate-200 rounded-lg px-2 py-1.5 text-xs font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-[var(--brand-blue)]" value={selectedOrgId} onChange={(e) => setSelectedOrgId(e.target.value)}>
                                        <option value="">Organization</option>
                                        {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                                    </select>
                                </label>
                            )
                    )}
                </div>

                <div className="flex gap-2">
                    <input
                        ref={inputRef}
                        type="text"
                        value={question}
                        onChange={(e) => setQuestion(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') ask(); }}
                        placeholder="What would you like to know?"
                        disabled={busy}
                        className="flex-1 bg-slate-50 border border-slate-200 text-slate-900 text-sm rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[var(--brand-blue)] placeholder:text-slate-400 disabled:opacity-60"
                    />
                    <button onClick={() => ask()} disabled={!question.trim() || busy} className="px-4 py-3 bg-[var(--brand-deep)] text-white rounded-xl hover:opacity-90 disabled:opacity-40 transition-opacity shadow-sm shrink-0 flex items-center gap-2 text-sm font-semibold">
                        {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Ask
                    </button>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                    {prompts.map((p) => (
                        <button key={p} onClick={() => ask(p)} disabled={busy} className="px-3 py-1.5 bg-white border border-slate-200 text-slate-600 hover:border-[var(--brand-blue)] hover:text-[var(--brand-deep)] rounded-full text-xs font-medium transition-colors disabled:opacity-50">
                            {p}
                        </button>
                    ))}
                </div>
            </div>

            {/* ANSWER */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                {busy && (
                    <div className="p-10 flex flex-col items-center justify-center text-center">
                        <Loader2 size={22} className="text-[var(--brand-blue)] animate-spin mb-3" />
                        <p className="text-sm font-semibold text-slate-700">Running the query</p>
                        <p className="text-xs text-slate-400 mt-1">Writing it, checking it, reading the warehouse at the floor.</p>
                    </div>
                )}
                {!busy && !result && (
                    <div className="p-10 text-center">
                        <p className="text-sm font-semibold text-slate-600">Ask a question, or pick one above.</p>
                        <p className="text-xs text-slate-400 mt-1">The answer arrives as a chart with a one-line reading and the query behind it.</p>
                    </div>
                )}
                {!busy && result && (
                    <div className="p-6 md:p-8 space-y-4">
                        <div>
                            <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-widest">You asked</p>
                            <h3 className="text-lg font-semibold text-[var(--brand-deep)] leading-snug">{result.question}</h3>
                        </div>
                        {result.error
                            ? <p className="text-sm text-rose-700 bg-rose-50 border border-rose-100 rounded-lg px-4 py-3">{result.error}</p>
                            : result.notice
                                ? <p className="text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-4 py-3 leading-relaxed">{result.text}</p>
                                : <p className="text-sm text-slate-700 leading-relaxed">{result.text}</p>}
                        {!result.error && !result.notice && result.data?.length > 0 && <AnswerChart result={result} colors={colors} />}
                        {!result.error && !result.notice && result.data?.length === 0 && (
                            <p className="text-xs text-slate-500 flex items-center gap-1.5"><Lock size={12} /> Nothing cleared the floor for that question. A broader grouping, by month or across the whole organization, usually does.</p>
                        )}
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 pt-3 border-t border-slate-100">
                            {(result.error || result.notice)
                                ? <p className="text-[11px] text-slate-400 flex items-center gap-1.5"><Lock size={11} /> Nothing was read from the warehouse for this one.</p>
                                : <p className="text-[11px] text-slate-400 flex items-center gap-1.5"><Lock size={11} /> Groups of {MIN_GROUP_SIZE} or more. Rows built from fewer people were dropped before this answer returned.</p>}
                            {result.sql && (
                                <button onClick={() => setShowSql(v => !v)} className="text-[11px] font-semibold text-[var(--brand-blue)] flex items-center gap-1 self-start md:self-auto">
                                    {showSql ? <ChevronUp size={13} /> : <ChevronDown size={13} />} {showSql ? 'Hide the query' : (result.notice ? 'Show the attempt' : 'Show the query it ran')}
                                </button>
                            )}
                        </div>
                        {showSql && result.sql && (
                            <div className="space-y-2">
                                {result.reason && <p className="text-[11px] text-slate-500"><span className="font-semibold text-slate-600">Why it stopped:</span> {result.reason}</p>}
                                <pre className="text-[11px] leading-relaxed text-slate-600 bg-slate-50 border border-slate-100 rounded-lg p-4 overflow-x-auto whitespace-pre-wrap">{result.sql}</pre>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* EARLIER QUESTIONS */}
            {history.length > 0 && (
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 md:p-8">
                    <div className="flex items-center justify-between mb-3">
                        <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-widest flex items-center gap-1.5"><Clock3 size={12} /> Earlier questions</p>
                        <button onClick={clearHistory} className="text-[11px] font-semibold text-slate-400 hover:text-slate-600 flex items-center gap-1"><Trash2 size={12} /> Clear</button>
                    </div>
                    <div className="divide-y divide-slate-100">
                        {history.map((h) => (
                            <button key={h.at} onClick={() => { setResult(h); setShowSql(false); }} className={`w-full text-left py-2.5 flex items-center justify-between gap-4 hover:text-[var(--brand-deep)] ${result?.at === h.at ? 'text-[var(--brand-deep)]' : 'text-slate-700'}`}>
                                <span className="text-sm font-medium truncate">{h.question}</span>
                                <span className="text-[11px] text-slate-400 shrink-0 tabular-nums">{when(h.at)}</span>
                            </button>
                        ))}
                    </div>
                    <p className="text-[10px] text-slate-400 mt-3">Kept in this browser only, for this organization.</p>
                </div>
            )}

            <DisciplineFooter caption="The analyst reads the same warehouse as every other tab, at the same floor. Its one-line reading describes the chart; it is not a forecast and never describes a person." showLegend={false} />
        </div>
    );
};

export default AnalystPanel;
